import React, { useState, useEffect, useRef } from "react";
import { useChanges, useEditorData } from "./AdminDashboard";
import { Change } from "./useChangesTracker";
import { fetchSection } from "../../services/api";
import eventosData from "../../eventos.json";
import { getFieldChanges } from "./deepCompare";
import "./Admin.css";

interface Evento {
  id: number;
  titulo: {
    pt: string;
    en: string;
    es: string;
  };
  descricao: {
    pt: string;
    en: string;
    es: string;
  };
  data: string;
  url?: string;
}

interface EventosEditorProps {
  onEditingChange?: (isEditing: boolean) => void;
}

const EventosEditor: React.FC<EventosEditorProps> = ({ onEditingChange }) => {
  const { changes: changesList, addChange, clearChange, savedSignal } =
    useChanges();
  const { registerEditor, unregisterEditor } = useEditorData();
  const [items, setItems] = useState<Evento[]>([]);
  const [originalItems, setOriginalItems] = useState<Evento[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState<Evento | null>(null);
  const [activeLang, setActiveLang] = useState<"pt" | "en" | "es">("pt");
  const [sortBy, setSortBy] = useState<"date" | "title">("date");

  useEffect(() => {
    // Load the live content from the worker, falling back to the bundled copy.
    let cancelled = false;
    (async () => {
      const data = await fetchSection<Evento[]>(
        "eventos",
        eventosData as Evento[]
      );
      if (cancelled) return;
      setItems(data);
      setOriginalItems(data);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Rebaseline after a successful save (no reload -> no stale KV read-back).
  const itemsRef = useRef(items);
  itemsRef.current = items;
  useEffect(() => {
    if (savedSignal > 0) setOriginalItems(itemsRef.current);
  }, [savedSignal]);

  useEffect(() => {
    // Track changes by comparing current state with original
    if (loading) return;

    // Clear all old changes for this section first
    items.forEach((p) => {
      clearChange(`evento-${p.id}-add`);
      clearChange(`evento-${p.id}-edit`);
      clearChange(`evento-${p.id}-delete`);
    });
    originalItems.forEach((p) => {
      clearChange(`evento-${p.id}-add`);
      clearChange(`evento-${p.id}-edit`);
      clearChange(`evento-${p.id}-delete`);
    });

    // Track new changes
    items.forEach((item) => {
      const original = originalItems.find((p) => p.id === item.id);
      if (!original) {
        addChange({
          id: `evento-${item.id}-add`,
          section: "Eventos",
          type: "add",
          description: `Adicionar: ${item.titulo.pt || "Novo evento"}`,
        });
      } else {
        const fieldChanges = getFieldChanges(original, item);
        if (fieldChanges.length > 0) {
          addChange({
            id: `evento-${item.id}-edit`,
            section: "Eventos",
            type: "edit",
            description: `Editar: ${item.titulo.pt || "Evento sem título"}`,
            fieldChanges,
          });
        }
      }
    });

    // Track deletions (only for items that existed in original data)
    originalItems.forEach((item) => {
      if (!items.find((i) => i.id === item.id)) {
        addChange({
          id: `evento-${item.id}-delete`,
          section: "Eventos",
          type: "delete",
          description: `Excluir: ${item.titulo.pt || "Evento sem título"}`,
        });
      }
    });
  }, [items, originalItems, addChange, clearChange, loading]);

  useEffect(() => {
    onEditingChange?.(editingItem !== null);
  }, [editingItem, onEditingChange]);

  // Register data getter for AdminDashboard
  useEffect(() => {
    const getData = () => [{ section: "eventos", data: items }];
    registerEditor("Eventos", getData);
    return () => unregisterEditor("Eventos");
  }, [items, registerEditor, unregisterEditor]);

  const addItem = () => {
    const newItem: Evento = {
      id: Math.max(...items.map((i) => i.id), 0) + 1,
      titulo: { pt: "", en: "", es: "" },
      descricao: { pt: "", en: "", es: "" },
      data: "",
      url: "",
    };
    setItems([...items, newItem]);
    setEditingItem(newItem);
  };

  const updateItem = (updatedItem: Evento) => {
    setItems(items.map((i) => (i.id === updatedItem.id ? updatedItem : i)));
    setEditingItem(null);
  };

  const deleteItem = (id: number) => {
    if (window.confirm("Tem certeza que deseja excluir este evento?")) {
      // Check if this item existed in original data
      const wasOriginal = originalItems.find((i) => i.id === id);

      setItems(items.filter((i) => i.id !== id));

      // If it was a newly added item (not in original), clear the add change
      if (!wasOriginal) {
        clearChange(`evento-${id}-add`);
        clearChange(`evento-${id}-edit`);
      }
    }
  };

  if (loading) return <div className="loading">Carregando...</div>;

  return (
    <div className="editor-container">
      <div className="editor-header">
        <h2>Gerenciar Eventos</h2>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as "date" | "title")}
            style={{
              padding: "8px 12px",
              borderRadius: "6px",
              border: "1px solid #ddd",
            }}
          >
            <option value="date">Ordenar por Data</option>
            <option value="title">Ordenar por Título</option>
          </select>
          <button onClick={addItem} className="add-button">
            Adicionar Evento
          </button>
        </div>
      </div>

      <div className="item-list">
        {[...items]
          .sort((a, b) => {
            if (sortBy === "date") {
              return (
                new Date(b.data.split("/").reverse().join("-")).getTime() -
                new Date(a.data.split("/").reverse().join("-")).getTime()
              );
            } else {
              return a.titulo.pt.localeCompare(b.titulo.pt);
            }
          })
          .map((item) => {
            const hasChanges = changesList.some((c: Change) =>
              c.id.includes(`evento-${item.id}`)
            );

            return (
              <div
                key={item.id}
                className={`item-card ${hasChanges ? "item-card-changed" : ""}`}
              >
                <div>
                  <h3>{item.titulo.pt || "Sem título"}</h3>
                  <p>{item.data}</p>
                </div>
                <div className="item-actions">
                  <button
                    onClick={() => setEditingItem(item)}
                    className="edit-button"
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => deleteItem(item.id)}
                    className="delete-button"
                  >
                    Excluir
                  </button>
                </div>
              </div>
            );
          })}
      </div>

      {editingItem && (
        <EventoModal
          item={editingItem}
          onClose={() => setEditingItem(null)}
          onSave={updateItem}
          activeLang={activeLang}
          setActiveLang={setActiveLang}
        />
      )}
    </div>
  );
};

interface EventoModalProps {
  item: Evento;
  onClose: () => void;
  onSave: (item: Evento) => void;
  activeLang: "pt" | "en" | "es";
  setActiveLang: (lang: "pt" | "en" | "es") => void;
}

const EventoModal: React.FC<EventoModalProps> = ({
  item,
  onClose,
  onSave,
  activeLang,
  setActiveLang,
}) => {
  const [formData, setFormData] = useState<Evento>(item);

  const handleSave = () => {
    onSave(formData);
  };

  const langs = ["pt", "en", "es"] as const;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>Editar Evento</h2>

        <div className="language-tabs">
          {langs.map((lang) => (
            <button
              key={lang}
              className={`language-tab ${activeLang === lang ? "active" : ""}`}
              onClick={() => setActiveLang(lang)}
            >
              {lang.toUpperCase()}
            </button>
          ))}
        </div>

        <div className="form-group">
          <label>Título ({activeLang.toUpperCase()})</label>
          <input
            type="text"
            value={formData.titulo[activeLang]}
            onChange={(e) =>
              setFormData({
                ...formData,
                titulo: { ...formData.titulo, [activeLang]: e.target.value },
              })
            }
          />
        </div>

        <div className="form-group">
          <label>Descrição ({activeLang.toUpperCase()})</label>
          <textarea
            value={formData.descricao[activeLang]}
            onChange={(e) =>
              setFormData({
                ...formData,
                descricao: {
                  ...formData.descricao,
                  [activeLang]: e.target.value,
                },
              })
            }
          />
        </div>

        <div className="form-group">
          <label>Data (DD/MM/YYYY)</label>
          <input
            type="text"
            value={formData.data}
            onChange={(e) => setFormData({ ...formData, data: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>URL (opcional)</label>
          <input
            type="url"
            value={formData.url || ""}
            onChange={(e) => setFormData({ ...formData, url: e.target.value })}
          />
        </div>

        <div className="form-actions">
          <button onClick={onClose} className="cancel-button">
            Cancelar
          </button>
          <button onClick={handleSave} className="save-button">
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
};

export default EventosEditor;
